import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, LayersControl, LayerGroup, useMap } from 'react-leaflet';
import { Layers, Home, Building, School, Briefcase, ShoppingBag, Factory, Ruler, Download, Map as MapIcon, Lightbulb } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';

// Create custom marker icons
const createIcon = (color) => {
  return new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

// Custom station icon
const stationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Demand driver icons
const residentialIcon = createIcon('green');
const commercialIcon = createIcon('gold');
const industrialIcon = createIcon('grey');
const educationalIcon = createIcon('blue');
const medicalIcon = createIcon('red');
const shoppingIcon = createIcon('orange');

// Mock demand driver data
const generateDemandDrivers = (centerLat, centerLng) => {
  // Ensure coordinates are valid numbers
  if (typeof centerLat !== 'number' || typeof centerLng !== 'number') {
    centerLat = 13.7563;
    centerLng = 100.5018;
  }

  const demandDrivers = {
    residential: [],
    commercial: [],
    industrial: [],
    educational: [],
    medical: [],
    shopping: []
  };

  // Generate random points around the station
  const generatePoints = (count, type, minDist = 0.5, maxDist = 3) => {
    const points = [];
    for (let i = 0; i < count; i++) {
      // Random angle
      const angle = Math.random() * Math.PI * 2;
      // Random distance (in km)
      const distance = (Math.random() * (maxDist - minDist) + minDist) / 111; // convert km to degrees (approx)
      
      const lat = centerLat + distance * Math.sin(angle);
      const lng = centerLng + distance * Math.cos(angle);
      
      points.push({
        id: `${type}-${i}`,
        name: `${type.charAt(0).toUpperCase() + type.slice(1)} Area ${i + 1}`,
        type: type,
        lat,
        lng,
        importance: Math.floor(Math.random() * 5) + 1 // 1-5
      });
    }
    return points;
  };

  demandDrivers.residential = generatePoints(10, 'residential');
  demandDrivers.commercial = generatePoints(5, 'commercial', 0.8, 2.5);
  demandDrivers.industrial = generatePoints(3, 'industrial', 1.5, 4);
  demandDrivers.educational = generatePoints(4, 'educational', 0.7, 2.8);
  demandDrivers.medical = generatePoints(2, 'medical', 0.6, 2.2);
  demandDrivers.shopping = generatePoints(3, 'shopping', 0.5, 2);

  return demandDrivers;
};

// Mock competitor data
const generateCompetitors = (centerLat, centerLng) => {
  // Ensure coordinates are valid numbers
  if (typeof centerLat !== 'number' || typeof centerLng !== 'number') {
    centerLat = 13.7563;
    centerLng = 100.5018;
  }
  
  const competitors = [];
  
  // Generate random competitors around the station
  for (let i = 0; i < 5; i++) {
    // Random angle
    const angle = Math.random() * Math.PI * 2;
    // Random distance (in km)
    const distance = (Math.random() * 2.5 + 0.5) / 111; // convert km to degrees (approx)
    
    const lat = centerLat + distance * Math.sin(angle);
    const lng = centerLng + distance * Math.cos(angle);
    
    competitors.push({
      id: i + 1,
      name: `Competitor ${String.fromCharCode(65 + i)}`, // A, B, C, etc.
      lat,
      lng,
      price: { 
        diesel: (31.50 + Math.random() * 0.5).toFixed(2), 
        gasohol95: (37.30 + Math.random() * 0.6).toFixed(2) 
      },
      volume: Math.floor(Math.random() * 8000) + 4000, // 4000-12000
      threat: Math.floor(Math.random() * 3) + 1 // 1-3 (low, medium, high)
    });
  }
  
  return competitors;
};

// Helper component to set the map view
const MapViewSetter = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && Array.isArray(center) && center.length === 2) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
};

const MarketOverview = ({ station, onViewDetailed }) => {
  // Handle case where station is undefined or missing coordinates with more robust defensive programming
  const stationWithDefaults = station && typeof station === 'object' ? station : {};
  const validStation = {
    id: stationWithDefaults.id ?? 'Unknown',
    name: stationWithDefaults.name ?? 'Unknown Station',
    format: stationWithDefaults.format ?? 'Standard',
    area: stationWithDefaults.area ?? 'Unknown Area',
    volume: stationWithDefaults.volume ?? 10000,
    margin: stationWithDefaults.margin ?? 0.8,
    competitors: stationWithDefaults.competitors ?? 0,
    lat: typeof stationWithDefaults.lat === 'number' ? stationWithDefaults.lat : 13.7563, // Default to Bangkok
    lng: typeof stationWithDefaults.lng === 'number' ? stationWithDefaults.lng : 100.5018
  };

  const [mapType, setMapType] = useState('standard');
  const [visibleLayers, setVisibleLayers] = useState({
    residential: true,
    commercial: true,
    industrial: true,
    educational: true,
    medical: true,
    shopping: true,
    competitors: true
  });
  const [distanceRings, setDistanceRings] = useState({
    ring1km: true,
    ring3km: true,
    ring5km: false
  });
  
  const navigate = useNavigate();
  
  // Generate demand drivers and competitors based on the station with defensive programming
  const demandDrivers = React.useMemo(() => 
    generateDemandDrivers(validStation.lat, validStation.lng), 
    [validStation.lat, validStation.lng]
  );
  
  const competitors = React.useMemo(() => 
    generateCompetitors(validStation.lat, validStation.lng), 
    [validStation.lat, validStation.lng]
  );

  // Market statistics data
  const marketStats = {
    population: Math.floor(Math.random() * 30000) + 20000,
    traffic: Math.floor(Math.random() * 20000) + 10000,
    marketPenetration: Math.floor(Math.random() * 20) + 10,
    competitiveIntensity: Math.floor(Math.random() * 5) + 3,
    marketShare: Math.floor(Math.random() * 25) + 15
  };

  const toggleLayer = (layer) => {
    setVisibleLayers({
      ...visibleLayers,
      [layer]: !visibleLayers[layer]
    });
  };

  const toggleDistanceRing = (ring) => {
    setDistanceRings({
      ...distanceRings,
      [ring]: !distanceRings[ring]
    });
  };

  const handleViewDetailedAnalysis = () => {
    if (onViewDetailed) {
      onViewDetailed();
    } else {
      // Fallback to route navigation
      navigate(`/trade-area-analyzer/station/${validStation.id}`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Trade Area Map</h3>
        <div className="flex items-center space-x-2">
          <button 
            className={`px-3 py-1 text-sm font-medium rounded ${mapType === 'standard' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setMapType('standard')}
          >
            Standard
          </button>
          <button 
            className={`px-3 py-1 text-sm font-medium rounded ${mapType === 'satellite' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setMapType('satellite')}
          >
            Satellite
          </button>
          <button 
            className={`px-3 py-1 text-sm font-medium rounded ${mapType === 'hybrid' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setMapType('hybrid')}
          >
            Hybrid
          </button>
        </div>
      </div>

      {/* Layer Controls */}
      <div className="bg-white rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="font-medium text-sm text-gray-700 mr-2 flex items-center">
          <Layers size={16} className="mr-1" /> Layers:
        </div>

        {/* Demand Drivers */}
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${visibleLayers.residential ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleLayer('residential')}
        >
          <Home size={14} className="mr-1" /> Residential
        </button>
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${visibleLayers.commercial ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleLayer('commercial')}
        >
          <Building size={14} className="mr-1" /> Commercial
        </button>
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${visibleLayers.industrial ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleLayer('industrial')}
        >
          <Factory size={14} className="mr-1" /> Industrial
        </button>
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${visibleLayers.educational ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleLayer('educational')}
        >
          <School size={14} className="mr-1" /> Educational
        </button>
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${visibleLayers.medical ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleLayer('medical')}
        >
          <Briefcase size={14} className="mr-1" /> Medical
        </button>
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${visibleLayers.shopping ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleLayer('shopping')}
        >
          <ShoppingBag size={14} className="mr-1" /> Shopping
        </button>

        <div className="h-4 border-r border-gray-300 mx-1"></div>

        {/* Competitor Layer */}
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${visibleLayers.competitors ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleLayer('competitors')}
        >
          <MapIcon size={14} className="mr-1" /> Competitors
        </button>
        
        <div className="h-4 border-r border-gray-300 mx-1"></div>
        
        {/* Distance Rings */}
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${distanceRings.ring1km ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleDistanceRing('ring1km')}
        >
          <Ruler size={14} className="mr-1" /> 1km Ring
        </button>
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${distanceRings.ring3km ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleDistanceRing('ring3km')}
        >
          <Ruler size={14} className="mr-1" /> 3km Ring
        </button>
        <button 
          className={`px-2 py-1 text-xs rounded flex items-center ${distanceRings.ring5km ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => toggleDistanceRing('ring5km')}
        >
          <Ruler size={14} className="mr-1" /> 5km Ring
        </button>
      </div>

      {/* Map Container */}
      <div className="h-96 w-full bg-gray-100 rounded-md overflow-hidden mb-6">
        <MapContainer
          center={[validStation.lat, validStation.lng]}
          zoom={14}
          style={{ width: '100%', height: '100%' }}
        >
          <MapViewSetter center={[validStation.lat, validStation.lng]} zoom={14} />

          {/* Map Tiles */}
          {mapType === 'standard' && (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}
          {mapType === 'satellite' && (
            <TileLayer
              attribution='&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          )}
          {mapType === 'hybrid' && (
            <>
              <TileLayer
                attribution='&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://stamen-tiles-{s}.a.ssl.fastly.net/toner-lines/{z}/{x}/{y}{r}.png"
                opacity={0.7}
              />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://stamen-tiles-{s}.a.ssl.fastly.net/toner-labels/{z}/{x}/{y}{r}.png"
                opacity={0.7}
              />
            </>
          )}

          {/* Station Marker */}
          <Marker position={[validStation.lat, validStation.lng]} icon={stationIcon}>
            <Popup>
              <div className="font-medium">{validStation.name}</div>
              <div className="text-sm text-gray-600">{validStation.format} Station</div>
              <div className="text-sm text-gray-600">Volume: {validStation.volume.toLocaleString()} L/day</div>
              <div className="mt-2">
                <button 
                  className="w-full bg-purple-600 text-white text-xs py-1 px-2 rounded hover:bg-purple-700"
                  onClick={handleViewDetailedAnalysis}
                >
                  View Detailed Analysis
                </button>
              </div>
            </Popup>
          </Marker>

          {/* Distance Rings */}
          {distanceRings.ring1km && (
            <Circle
              center={[validStation.lat, validStation.lng]}
              radius={1000}
              pathOptions={{ color: '#A100FF', fillColor: '#A100FF', fillOpacity: 0.05, weight: 1, dashArray: '5, 5' }}
            />
          )}
          {distanceRings.ring3km && (
            <Circle
              center={[validStation.lat, validStation.lng]}
              radius={3000}
              pathOptions={{ color: '#A100FF', fillColor: '#A100FF', fillOpacity: 0.03, weight: 1, dashArray: '5, 5' }}
            />
          )}
          {distanceRings.ring5km && (
            <Circle
              center={[validStation.lat, validStation.lng]}
              radius={5000}
              pathOptions={{ color: '#A100FF', fillColor: '#A100FF', fillOpacity: 0.02, weight: 1, dashArray: '5, 5' }}
            />
          )}

          {/* Layer Groups */}
          <LayersControl position="topright">
            {/* Residential Areas */}
            {visibleLayers.residential && demandDrivers.residential.map(point => (
              <Marker key={point.id} position={[point.lat, point.lng]} icon={residentialIcon}>
                <Popup>
                  <div className="font-medium">{point.name}</div>
                  <div className="text-sm text-gray-600">Type: Residential</div>
                  <div className="text-sm text-gray-600">Importance: {point.importance}/5</div>
                </Popup>
              </Marker>
            ))}
            
            {/* Commercial Areas */}
            {visibleLayers.commercial && demandDrivers.commercial.map(point => (
              <Marker key={point.id} position={[point.lat, point.lng]} icon={commercialIcon}>
                <Popup>
                  <div className="font-medium">{point.name}</div>
                  <div className="text-sm text-gray-600">Type: Commercial</div>
                  <div className="text-sm text-gray-600">Importance: {point.importance}/5</div>
                </Popup>
              </Marker>
            ))}
            
            {/* Industrial Areas */}
            {visibleLayers.industrial && demandDrivers.industrial.map(point => (
              <Marker key={point.id} position={[point.lat, point.lng]} icon={industrialIcon}>
                <Popup>
                  <div className="font-medium">{point.name}</div>
                  <div className="text-sm text-gray-600">Type: Industrial</div>
                  <div className="text-sm text-gray-600">Importance: {point.importance}/5</div>
                </Popup>
              </Marker>
            ))}
            
            {/* Educational Institutions */}
            {visibleLayers.educational && demandDrivers.educational.map(point => (
              <Marker key={point.id} position={[point.lat, point.lng]} icon={educationalIcon}>
                <Popup>
                  <div className="font-medium">{point.name}</div>
                  <div className="text-sm text-gray-600">Type: Educational</div>
                  <div className="text-sm text-gray-600">Importance: {point.importance}/5</div>
                </Popup>
              </Marker>
            ))}
            
            {/* Medical Facilities */}
            {visibleLayers.medical && demandDrivers.medical.map(point => (
              <Marker key={point.id} position={[point.lat, point.lng]} icon={medicalIcon}>
                <Popup>
                  <div className="font-medium">{point.name}</div>
                  <div className="text-sm text-gray-600">Type: Medical</div>
                  <div className="text-sm text-gray-600">Importance: {point.importance}/5</div>
                </Popup>
              </Marker>
            ))}
            
            {/* Shopping Centers */}
            {visibleLayers.shopping && demandDrivers.shopping.map(point => (
              <Marker key={point.id} position={[point.lat, point.lng]} icon={shoppingIcon}>
                <Popup>
                  <div className="font-medium">{point.name}</div>
                  <div className="text-sm text-gray-600">Type: Shopping</div>
                  <div className="text-sm text-gray-600">Importance: {point.importance}/5</div>
                </Popup>
              </Marker>
            ))}
            
            {/* Competitors */}
            {visibleLayers.competitors && competitors.map(comp => (
              <Marker 
                key={comp.id} 
                position={[comp.lat, comp.lng]} 
                icon={createIcon(comp.threat === 3 ? 'red' : comp.threat === 2 ? 'orange' : 'yellow')}
              >
                <Popup>
                  <div className="font-medium">{comp.name}</div>
                  <div className="text-sm text-gray-600">
                    Diesel: {comp.price.diesel} ฿, Gasohol95: {comp.price.gasohol95} ฿
                  </div>
                  <div className="text-sm text-gray-600">
                    Est. Volume: {comp.volume.toLocaleString()} L/day
                  </div>
                  <div className="text-sm text-gray-600">
                    Threat Level: {comp.threat === 3 ? 'High' : comp.threat === 2 ? 'Medium' : 'Low'}
                  </div>
                </Popup>
              </Marker>
            ))}
          </LayersControl>
        </MapContainer>
      </div>

      {/* Market Summary Panel */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="text-md font-medium text-gray-900 mb-3">Market Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500 mb-1">Total Population</div>
            <div className="text-lg font-medium text-gray-900">{marketStats.population.toLocaleString()}</div>
            <div className="text-xs text-green-600 mt-1">+5.2% YoY</div>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500 mb-1">Traffic Volume</div>
            <div className="text-lg font-medium text-gray-900">{marketStats.traffic.toLocaleString()} vehicles/day</div>
            <div className="text-xs text-green-600 mt-1">+3.8% YoY</div>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500 mb-1">Market Penetration</div>
            <div className="text-lg font-medium text-gray-900">{marketStats.marketPenetration}%</div>
            <div className="text-xs text-green-600 mt-1">+2.1% YoY</div>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500 mb-1">Competitive Intensity</div>
            <div className="text-lg font-medium text-gray-900">{marketStats.competitiveIntensity}/10</div>
            <div className="text-xs text-red-600 mt-1">+0.7 pts YoY</div>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500 mb-1">Market Share</div>
            <div className="text-lg font-medium text-gray-900">{marketStats.marketShare}%</div>
            <div className="text-xs text-green-600 mt-1">+1.3% YoY</div>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          <p>Primary catchment area shows strong growth in residential population and commercial development, with a moderate competitive landscape. Peak traffic patterns observed during weekday morning (7-9am) and evening (5-7pm) hours.</p>
          <p className="mt-2">Key demand drivers include 3 shopping centers, 2 industrial zones, and 4 educational institutions within the 3km radius.</p>
        </div>
        <div className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
          <h5 className="font-medium text-gray-900 mb-2 flex items-center">
            <Lightbulb size={16} className="text-yellow-500 mr-2" /> AI Generated Market Insights
          </h5>
          <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2">
            <li>Morning commuters (6-9 AM) represent 25% of total volume but only 15% of convenience store sales. Opportunity for breakfast-oriented promotions.</li>
            <li>Station experiences 32% reduced volume during periods of high school traffic. Consider youth-targeted promotions during peak student transit times.</li>
            <li>All retail sites with newly opened regional-minor competitor in close proximity (&gt;0.5km) are losing fuel volume compared to the rest of the network. Recommend action is a developing targeted loyalty promotion program with higher points per litre</li>
          </ul>
        </div>
        <div className="flex justify-between mt-4">
          <button 
            className="text-sm flex items-center text-purple-600 hover:text-purple-800 hover:underline"
            onClick={handleViewDetailedAnalysis}
          >
            View Detailed Station Analysis
          </button>
          <button className="text-sm flex items-center text-purple-600 hover:text-purple-800">
            <Download size={14} className="mr-1" /> Download Full Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketOverview;
